import React, { useEffect, useState } from "react";
import api, { formatDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plate, COUNTRY_META } from "@/components/Plate";
import { PencilSimple, Trash, LinkSimple, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";

const WEBHOOK_API_KEY = "webhook-secret-otopark-2026";

const COUNTRY_OPTIONS = Object.entries(COUNTRY_META).map(([code, m]) => ({
  code, label: `${m.flag} ${m.name}`,
}));

export default function CameraTest() {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === "super_admin";
  const [plate, setPlate] = useState("34ABC123");
  const [cameraId, setCameraId] = useState("CAM-UST-01");
  const [direction, setDirection] = useState("auto");
  const [ulke, setUlke] = useState("TR");
  const [reads, setReads] = useState([]);
  const [editing, setEditing] = useState(null);

  // Hardware ID → camera mapping (Iter 22)
  const [cameras, setCameras] = useState([]);
  const [newHwId, setNewHwId] = useState("");
  const [newHwCameraId, setNewHwCameraId] = useState("CAM-UST-01");
  const [hwBusy, setHwBusy] = useState(false);

  const loadCameras = async () => {
    try {
      const { data } = await api.get("/cameras");
      setCameras(data || []);
    } catch { /* ignore */ }
  };

  const addHardwareId = async () => {
    const hw = (newHwId || "").trim();
    if (!hw) return toast.error("Seri numarası boş olamaz");
    setHwBusy(true);
    try {
      await api.post(`/cameras/${newHwCameraId}/hardware-ids`, { hardware_id: hw });
      toast.success(`${hw} → ${newHwCameraId} bağlandı`);
      setNewHwId("");
      loadCameras();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bağlanamadı");
    } finally { setHwBusy(false); }
  };

  const removeHardwareId = async (cameraId, hwId) => {
    if (!window.confirm(`${hwId} seri numarası ${cameraId} kamerasından silinsin mi?`)) return;
    try {
      await api.delete(`/cameras/${cameraId}/hardware-ids/${encodeURIComponent(hwId)}`);
      toast.success("Eşleşme silindi");
      loadCameras();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const load = async () => {
    const { data } = await api.get("/camera-reads?limit=30");
    setReads(data);
  };

  const openEdit = (r) => setEditing({
    id: r.id, plate: r.plate, camera_id: r.camera_id,
    parking_lot_key: r.parking_lot_key || "ust_kat",
    resolved_direction: r.resolved_direction || "auto",
  });
  const saveEdit = async () => {
    if (!editing?.plate) return toast.error("Plaka boş olamaz");
    try {
      await api.patch(`/camera-reads/${editing.id}`, {
        plate: editing.plate.toUpperCase(),
        camera_id: editing.camera_id,
        parking_lot_key: editing.parking_lot_key,
        resolved_direction: editing.resolved_direction,
      });
      toast.success("Kamera okuması güncellendi");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Güncellenemedi"); }
  };
  const deleteRead = async (r) => {
    if (!window.confirm(`Bu kamera okumasını silmek istediğinize emin misiniz?\n\nPlaka: ${r.plate}\nKamera: ${r.camera_id}\nZaman: ${new Date(r.timestamp).toLocaleString("tr-TR")}`)) return;
    try {
      await api.delete(`/camera-reads/${r.id}`);
      toast.success("Okuma silindi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  useEffect(() => {
    load();
    loadCameras();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  const send = async () => {
    try {
      const body = {
        plaka: plate.toUpperCase(),
        kamera_id: cameraId,
        yon: direction === "auto" ? null : direction,
        ulke: ulke === "TR" ? null : ulke,
        zaman: new Date().toISOString(),
        resim_url: "https://images.unsplash.com/photo-1512495039889-52a3b799c9bc?w=400",
      };
      const { data } = await axios.post(`${API}/plaka-oku`, body, {
        headers: { "X-API-Key": WEBHOOK_API_KEY, "Content-Type": "application/json" },
      });
      if (data.ok) {
        const cFlag = data.country?.flag || "";
        toast.success(`${cFlag} ${data.direction === "giris" ? "Giriş" : "Çıkış"} kaydedildi`);
      } else {
        toast.error(`Reddedildi: ${data.reason}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">ANPR Webhook</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Kamera Test</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Dışarıdaki ANPR kameraları veya tanıma yazılımı <code className="bg-muted px-1.5 py-0.5 rounded-sm text-xs">POST /api/plaka-oku</code> adresine
          <code className="bg-muted px-1.5 py-0.5 rounded-sm text-xs mx-1">X-API-Key</code> header&apos;ı ile plaka gönderir. Aşağıdaki form o çağrıyı simüle eder.
        </p>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label>Plaka</Label>
              <Input data-testid="camtest-plate" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} className="rounded-sm font-plate uppercase" />
            </div>
            <div>
              <Label>Ülke</Label>
              <Select value={ulke} onValueChange={setUlke}>
                <SelectTrigger className="rounded-sm" data-testid="camtest-country"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="TR">Otomatik / Türkiye</SelectItem>
                  {COUNTRY_OPTIONS.filter(c => c.code !== "TR" && c.code !== "XX").map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kamera ID</Label>
              <Select value={cameraId} onValueChange={setCameraId}>
                <SelectTrigger className="rounded-sm" data-testid="camtest-cam"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CAM-UST-01">CAM-UST-01 (Üst Kat)</SelectItem>
                  <SelectItem value="CAM-ALT-01">CAM-ALT-01 (Alt Kat)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Yön</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger className="rounded-sm" data-testid="camtest-dir"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Otomatik (önerilen)</SelectItem>
                  <SelectItem value="giris">Giriş</SelectItem>
                  <SelectItem value="cikis">Çıkış</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="camtest-send" onClick={send} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              Gönder
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Kamera Donanımı Bağla (Iter 22) — Dahua/Hikvision device_id → sistem kamerası eşleşmesi */}
      <Card className="rounded-sm border-border" data-testid="hw-map-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
                <LinkSimple size={16} className="text-primary" /> Kamera Donanımı Bağla
              </h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                Dahua ITSAPI kameralar <code className="bg-muted px-1 rounded">DeviceID</code> (seri numarası) ile POST yapar
                — örn. <code className="bg-muted px-1 rounded">CB04CB4PAJ3083A</code>.
                Bu seri numarasını sistemdeki kameraya bağladığında, kameradan gelen plaka
                doğru otoparka (üst/alt) atanır. Yeni kamera taktığında sadece burdan eşleştir; kod
                değişikliğine gerek yok.
              </p>
            </div>
          </div>

          {/* Yeni eşleştirme formu */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3 items-end mb-5 p-4 bg-muted/40 rounded-sm">
            <div>
              <Label className="text-xs">Kamera Seri Numarası (Hardware ID / DeviceID)</Label>
              <Input
                value={newHwId}
                onChange={(e) => setNewHwId(e.target.value.toUpperCase())}
                placeholder="CB04CB4PAJEA72F"
                className="rounded-sm font-mono uppercase"
                data-testid="hw-map-newid-input"
                onKeyDown={(e) => e.key === "Enter" && !hwBusy && addHardwareId()}
              />
            </div>
            <div>
              <Label className="text-xs">Hangi Kamera?</Label>
              <Select value={newHwCameraId} onValueChange={setNewHwCameraId}>
                <SelectTrigger className="rounded-sm" data-testid="hw-map-cam-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cameras.length > 0 ? cameras.map(c => (
                    <SelectItem key={c.camera_id} value={c.camera_id}>
                      {c.camera_id} ({c.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"})
                    </SelectItem>
                  )) : (
                    <>
                      <SelectItem value="CAM-UST-01">CAM-UST-01 (Üst Kat)</SelectItem>
                      <SelectItem value="CAM-ALT-01">CAM-ALT-01 (Alt Kat)</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={addHardwareId}
              disabled={hwBusy || !newHwId.trim()}
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-semibold h-10"
              data-testid="hw-map-add-btn"
            >
              <Plus size={14} /> Bağla
            </Button>
          </div>

          {/* Mevcut eşleştirmeler */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Mevcut Eşleştirmeler
            </div>
            {cameras.length === 0 ? (
              <div className="text-xs text-muted-foreground p-4 text-center">Yükleniyor...</div>
            ) : (
              <div className="space-y-2">
                {cameras.map(cam => (
                  <div key={cam.camera_id} className="border border-border rounded-sm p-3" data-testid={`hw-map-cam-${cam.camera_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-mono text-sm font-semibold">{cam.camera_id}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {cam.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"}
                        </span>
                      </div>
                      <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
                        {(cam.hardware_ids || []).length} donanım
                      </span>
                    </div>
                    {(cam.hardware_ids || []).length === 0 ? (
                      <div className="text-xs text-muted-foreground italic">
                        Henüz donanım eşleşmesi yok — bu kamera yalnızca doğrudan {cam.camera_id} ID&apos;siyle push aldığında çalışır.
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {(cam.hardware_ids || []).map(hw => (
                          <div
                            key={hw}
                            className="inline-flex items-center gap-2 px-2 py-1 bg-primary/10 border border-primary/30 rounded-sm font-mono text-xs"
                            data-testid={`hw-map-chip-${hw}`}
                          >
                            <LinkSimple size={11} className="text-primary" />
                            <span>{hw}</span>
                            {isSuperAdmin && (
                              <button
                                onClick={() => removeHardwareId(cam.camera_id, hw)}
                                className="text-destructive hover:text-destructive/80 ml-1"
                                title="Süperadmin: Eşleşmeyi kaldır"
                                data-testid={`hw-map-remove-${hw}`}
                              >
                                <Trash size={11} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4">
            <h3 className="text-sm font-semibold uppercase tracking-widest">Son Kamera Okumaları</h3>
            <span className="text-xs text-muted-foreground flex items-center gap-1.5"><span className="pulse-dot" /> 5sn</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zaman</TableHead>
                <TableHead>Plaka</TableHead>
                <TableHead>Kamera</TableHead>
                <TableHead>Otopark</TableHead>
                <TableHead>Yön</TableHead>
                {isSuperAdmin && <TableHead className="text-right">İşlem</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {reads.length === 0 && <TableRow><TableCell colSpan={isSuperAdmin ? 6 : 5} className="text-center py-8 text-muted-foreground">Kayıt yok</TableCell></TableRow>}
              {reads.map((r) => (
                <TableRow key={r.id} data-testid={`camread-row-${r.id}`}>
                  <TableCell className="text-xs">{formatDateTime(r.timestamp)}</TableCell>
                  <TableCell><Plate country={r.country_code}>{r.plate}</Plate></TableCell>
                  <TableCell className="text-xs font-mono">{r.camera_id}</TableCell>
                  <TableCell className="uppercase text-xs tracking-widest">{r.parking_lot_key === "ust_kat" ? "Üst" : "Alt"}</TableCell>
                  <TableCell className="uppercase text-xs tracking-widest font-semibold">{r.resolved_direction}</TableCell>
                  {isSuperAdmin && (
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                                data-testid={`camread-edit-${r.id}`}
                                title="Süperadmin: Düzenle"
                                className="h-8 w-8 p-0 text-primary">
                          <PencilSimple size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteRead(r)}
                                data-testid={`camread-delete-${r.id}`}
                                title="Süperadmin: Sil"
                                className="h-8 w-8 p-0 text-destructive">
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* SÜPERADMİN — Kamera Okuma Düzenle */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kamera Okuması Düzenle</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
                ⚠️ Bu hareket yalnızca yanlış okunan plaka veya kamera ID gibi
                düzeltmeler için. Ödeme/oturum kayıtları etkilenmez.
              </div>
              <div>
                <Label>Plaka</Label>
                <Input value={editing.plate}
                       onChange={(e) => setEditing({ ...editing, plate: e.target.value.toUpperCase() })}
                       className="rounded-sm font-plate uppercase"
                       data-testid="camread-edit-plate" />
              </div>
              <div>
                <Label>Kamera ID</Label>
                <Input value={editing.camera_id}
                       onChange={(e) => setEditing({ ...editing, camera_id: e.target.value })}
                       className="rounded-sm font-mono"
                       data-testid="camread-edit-camera" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Otopark</Label>
                  <Select value={editing.parking_lot_key}
                          onValueChange={(v) => setEditing({ ...editing, parking_lot_key: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="camread-edit-lot"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ust_kat">Üst Kat</SelectItem>
                      <SelectItem value="alt_kat">Alt Kat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Yön</Label>
                  <Select value={editing.resolved_direction}
                          onValueChange={(v) => setEditing({ ...editing, resolved_direction: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="camread-edit-direction"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="giris">Giriş</SelectItem>
                      <SelectItem value="cikis">Çıkış</SelectItem>
                      <SelectItem value="cikis_no_open">Çıkış (Eşleşmedi)</SelectItem>
                      <SelectItem value="auto">Otomatik</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>İptal</Button>
            <Button onClick={saveEdit} data-testid="camread-edit-save"
                    className="bg-primary text-primary-foreground hover:bg-primary/90">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
